/**
 * useClientConversationTarget (KAN-736)
 *
 * What a client conversation needs when only the clientId is known (a shared
 * ?dialog= link, the /clients/:clientId/chat page): the client (for the title
 * and manual mode) and, without an estimate given, the client's latest
 * estimate with messages (manual messages are recorded into it).
 * Shared by TranscriptDialogHost (KAN-710) and ContactChatPage.
 */
import { useQuery } from '@tanstack/react-query'
import { useClient } from '@/services/api/clientQueries'
import { clientsApi } from '@/services/api/clients'
import type { Client } from '@/types/client'
import { clientTitle, pickTranscriptEstimate } from './useClientTranscript'

interface Options {
  clientId: string | null
  /** Estimate already known (from the URL); then estimates are not loaded. */
  estimateId?: string | null
  /** Load the client card (not needed when the caller knows the title). */
  loadClient?: boolean
}

export interface ClientConversationTarget {
  client: Client | undefined
  isClientLoading: boolean
  isClientError: boolean
  /** Estimate of the conversation; null until loaded or when there is none. */
  contactId: string | null
  /** Client name (or phone); '' until the client is loaded. */
  title: string
}

export function useClientConversationTarget({
  clientId,
  estimateId = null,
  loadClient = true,
}: Options): ClientConversationTarget {
  const clientQuery = useClient(clientId, loadClient)
  const { data: estimates } = useQuery({
    queryKey: ['client-estimates', clientId],
    queryFn: () => clientsApi.getEstimates(clientId as string),
    enabled: !!clientId && !estimateId,
  })
  const contactId =
    estimateId ?? (estimates ? (pickTranscriptEstimate(estimates)?.id ?? null) : null)
  const client = clientQuery.data
  return {
    client,
    isClientLoading: clientQuery.isLoading,
    isClientError: clientQuery.isError,
    contactId,
    title: client ? clientTitle(client) : '',
  }
}
